import React, { useState, useEffect } from 'react';
import { therapyApi, patientsApi, planTypesApi } from '../../services/api';
import toast from 'react-hot-toast';
import { HiX } from 'react-icons/hi';
import { usePermissions } from '../../hooks/usePermissions';
import { formatValidationErrors } from '../../utils/errorHandler';
import { getCached } from '../../services/lookupCache';

const TherapyPlanForm = ({ plan, patient, onClose, onSuccess }) => {
  const { canCreate, canUpdate } = usePermissions();
  const [loading, setLoading] = useState(false);
  const [therapyTypes, setTherapyTypes] = useState([]);
  const [planTypes, setPlanTypes] = useState([]);
  const [patients, setPatients] = useState([]);
  const [formData, setFormData] = useState({
    patient_id: patient?.id || '',
    therapy_type_ids: [], // Changed to array for multiple selection
    plan_type_id: '',
    start_date: '',
    end_date: '',
    start_time: '',
    end_time: '',
    sessions_per_week: 5,
    session_duration: 60,
    fee_per_session: '',
    notes: '',
    status: 'active'
  });
  const [selectedTherapyTypes, setSelectedTherapyTypes] = useState([]); // For checkbox selection

  const fetchTherapyTypes = async () => {
    try {
      const response = await getCached(
        'therapyTypes:active',
        () => therapyApi.getTypes({ is_active: true })
      );
      setTherapyTypes(response.data.data || []);
    } catch (error) {
      toast.error('Failed to fetch therapy types');
    }
  };

  const fetchPatients = async () => {
    try {
      const response = await patientsApi.getAll({ limit: 1000, status: 'active' });
      setPatients(response.data.data.patients || []);
    } catch (error) {
      toast.error('Failed to fetch patients');
    }
  };

  const fetchPlanTypes = async () => {
    try {
      const response = await planTypesApi.getAll({ is_active: true });
      setPlanTypes(response?.data?.data || []);
    } catch {
      toast.error('Failed to fetch plan types');
    }
  };


  useEffect(() => {
    fetchTherapyTypes();
    fetchPlanTypes();
    if (!patient) {
      fetchPatients();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient]);

  // Calculate session duration from start and end time
  const calculateSessionDuration = (startTime, endTime) => {
    if (!startTime || !endTime) {
      return 60; // Default to 60 minutes if times are not provided
    }

    try {
      const [startHours, startMinutes] = startTime.split(':').map(Number);
      const [endHours, endMinutes] = endTime.split(':').map(Number);

      const startTotalMinutes = startHours * 60 + startMinutes;
      const endTotalMinutes = endHours * 60 + endMinutes;

      // Handle case where end time is next day (e.g., 23:00 to 01:00)
      let duration = endTotalMinutes - startTotalMinutes;
      if (duration < 0) {
        duration = (24 * 60) + duration; // Add 24 hours
      }

      return duration > 0 ? duration : 60; // Return calculated duration or default to 60
    } catch (error) {
      return 60; // Default to 60 minutes on error
    }
  };

  // Load plan data when editing
  useEffect(() => {
    if (plan) {
      // Always fetch full plan details to ensure we have therapy types
      const loadPlanData = async () => {
        try {
          // Always fetch the full plan to get therapy types
          const response = await therapyApi.getPlanById(plan.id);
          const planData = response.data.data;
          
          // Extract therapy type IDs - ensure they are all strings
          let typeIds = [];
          if (planData.therapy_type_ids && Array.isArray(planData.therapy_type_ids)) {
            typeIds = planData.therapy_type_ids.map(id => id.toString());
          } else if (planData.therapy_types && Array.isArray(planData.therapy_types)) {
            typeIds = planData.therapy_types.map(t => t.id.toString());
          } else if (planData.therapy_type_id) {
            typeIds = [planData.therapy_type_id.toString()];
          }
          
          // Set selected therapy types
          setSelectedTherapyTypes(typeIds);
          
          // Calculate session duration from start and end time if both are provided
          const calculatedDuration = (planData.start_time && planData.end_time) 
            ? calculateSessionDuration(planData.start_time, planData.end_time)
            : (planData.session_duration || 60);
          
          // Set form data - dates are already formatted as 'YYYY-MM-DD' strings from backend
          setFormData({
            patient_id: planData.patient_id || '',
            therapy_type_ids: typeIds,
            plan_type_id: planData.plan_type_id ? String(planData.plan_type_id) : '',
            start_date: planData.start_date || '',
            end_date: planData.end_date || '',
            start_time: planData.start_time || '',
            end_time: planData.end_time || '',
            sessions_per_week: planData.sessions_per_week || 5,
            session_duration: calculatedDuration,
            fee_per_session: planData.fee_per_session || '',
            notes: planData.notes || '',
            status: planData.status || 'active'
          });
        } catch (error) {
          console.error('Failed to load plan data:', error);
          // Fallback to basic plan data
          const typeIds = plan.therapy_type_id ? [plan.therapy_type_id.toString()] : [];
          setSelectedTherapyTypes(typeIds);
          // Calculate session duration from start and end time if both are provided
          const calculatedDuration = (plan.start_time && plan.end_time) 
            ? calculateSessionDuration(plan.start_time, plan.end_time)
            : (plan.session_duration || 60);
          setFormData({
            patient_id: plan.patient_id || '',
            therapy_type_ids: typeIds,
            plan_type_id: plan.plan_type_id ? String(plan.plan_type_id) : '',
            start_date: plan.start_date ? (typeof plan.start_date === 'string' ? plan.start_date.split('T')[0] : plan.start_date) : '',
            end_date: plan.end_date ? (typeof plan.end_date === 'string' ? plan.end_date.split('T')[0] : plan.end_date) : '',
            start_time: plan.start_time || '',
            end_time: plan.end_time || '',
            sessions_per_week: plan.sessions_per_week || 5,
            session_duration: calculatedDuration,
            fee_per_session: plan.fee_per_session || '',
            notes: plan.notes || '',
            status: plan.status || 'active'
          });
        }
      };
      
      loadPlanData();
    } else if (patient) {
      setFormData(prev => ({
        ...prev,
        patient_id: patient.id
      }));
    }
  }, [plan, patient]);

  // Auto-calculate fee when therapy types are loaded and selected types change
  useEffect(() => {
    if (therapyTypes.length > 0 && selectedTherapyTypes.length > 0) {
      const calculatedFee = calculateTotalFee(selectedTherapyTypes);
      // Update fee when therapy types selection changes
      setFormData(prev => {
        // Only update if therapy type IDs have changed
        const prevIds = prev.therapy_type_ids.map(id => id.toString()).sort().join(',');
        const newIds = selectedTherapyTypes.sort().join(',');
        
        if (prevIds !== newIds) {
          return {
            ...prev,
            fee_per_session: calculatedFee
          };
        }
        return prev;
      });
    } else if (selectedTherapyTypes.length === 0) {
      // Clear fee if no therapy types selected
      setFormData(prev => ({
        ...prev,
        fee_per_session: ''
      }));
    }
  }, [selectedTherapyTypes, therapyTypes]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: name === 'sessions_per_week'
          ? parseInt(value) || 0 
          : name === 'fee_per_session'
          ? parseFloat(value) || ''
          : value
      };

      // Auto-calculate session duration when start_time or end_time changes
      if (name === 'start_time' || name === 'end_time') {
        const startTime = name === 'start_time' ? value : prev.start_time;
        const endTime = name === 'end_time' ? value : prev.end_time;
        newData.session_duration = calculateSessionDuration(startTime, endTime);
      }

      return newData;
    });
  };

  // Calculate total fee from selected therapy types
  const calculateTotalFee = (selectedIds) => {
    if (!selectedIds || selectedIds.length === 0) {
      return '';
    }
    
    const totalFee = selectedIds.reduce((sum, typeIdStr) => {
      const type = therapyTypes.find(t => t.id.toString() === typeIdStr.toString());
      if (type && type.fee) {
        return sum + parseFloat(type.fee);
      }
      return sum;
    }, 0);
    
    return totalFee > 0 ? totalFee.toFixed(2) : '';
  };

  const handleTherapyTypeCheckboxChange = (therapyTypeId, checked) => {
    let newSelectedTypes;
    if (checked) {
      newSelectedTypes = [...selectedTherapyTypes, therapyTypeId.toString()];
      setSelectedTherapyTypes(newSelectedTypes);
      setFormData(prev => ({
        ...prev,
        therapy_type_ids: [...prev.therapy_type_ids, therapyTypeId.toString()]
      }));
    } else {
      newSelectedTypes = selectedTherapyTypes.filter(id => id !== therapyTypeId.toString());
      setSelectedTherapyTypes(newSelectedTypes);
      setFormData(prev => ({
        ...prev,
        therapy_type_ids: prev.therapy_type_ids.filter(id => id !== therapyTypeId.toString())
      }));
    }
    
    // Auto-calculate fee per session from selected therapy types
    const calculatedFee = calculateTotalFee(newSelectedTypes);
    setFormData(prev => ({
      ...prev,
      fee_per_session: calculatedFee
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check permissions
    if (plan && !canUpdate('therapy')) {
      toast.error('You do not have permission to update therapy plans');
      return;
    }
    if (!plan && !canCreate('therapy')) {
      toast.error('You do not have permission to create therapy plans');
      return;
    }

    setLoading(true);

    // Validate therapy types
    if (formData.therapy_type_ids.length === 0) {
      toast.error('Please select at least one therapy type');
      setLoading(false);
      return;
    }
    if (!formData.plan_type_id) {
      toast.error('Please select a plan type');
      setLoading(false);
      return;
    }

    try {
      const submitData = {
        patient_id: parseInt(formData.patient_id),
        therapy_type_ids: formData.therapy_type_ids.map(id => parseInt(id)), // Array of therapy type IDs
        plan_type_id: parseInt(formData.plan_type_id),
        start_date: formData.start_date,
        end_date: formData.end_date || null,
        start_time: formData.start_time || null,
        end_time: formData.end_time || null,
        sessions_per_week: parseInt(formData.sessions_per_week) || 5,
        session_duration: parseInt(formData.session_duration) || 60,
        fee_per_session: formData.fee_per_session ? parseFloat(formData.fee_per_session) : null,
        notes: formData.notes || null,
        status: formData.status
      };

      if (plan) {
        await therapyApi.updatePlan(plan.id, submitData);
        toast.success('Therapy plan updated successfully');
      } else {
        await therapyApi.createPlan(submitData);
        toast.success('Therapy plan created successfully');
      }
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(formatValidationErrors(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-shell flex items-start justify-center overflow-y-auto py-4 sm:items-center">
      <div className="modal-panel max-w-3xl overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <h3 className="text-lg font-semibold">
            {plan ? 'Edit Therapy Plan' : 'Create Therapy Plan'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <HiX className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Patient Selection */}
          {!patient && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Patient <span className="text-red-500">*</span>
              </label>
              <select
                name="patient_id"
                value={formData.patient_id}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Select Patient</option>
                {patients.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.full_name} ({p.patient_code})
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Therapy Types - Multiple Selection with Checkboxes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Therapy Type <span className="text-red-500">*</span>
            </label>
            <div className="border border-gray-300 rounded-lg bg-white">
              <div className="max-h-48 overflow-y-auto p-3">
                {therapyTypes.length === 0 ? (
                  <p className="text-sm text-gray-500">Loading therapy types...</p>
                ) : (
                  <div className="space-y-2">
                    {therapyTypes.map((type) => {
                      const typeIdStr = type.id.toString();
                      // Ensure we're comparing strings with strings - normalize both sides
                      const isChecked = selectedTherapyTypes.some(id => id.toString() === typeIdStr);
                      return (
                        <label key={type.id} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded cursor-pointer">
                          <div className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={(e) => handleTherapyTypeCheckboxChange(type.id, e.target.checked)}
                              className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                            />
                            <span className="text-sm text-gray-700">{type.name}</span>
                          </div>
                          {type.fee && (
                            <span className="text-sm font-medium text-gray-600">
                              ₹{parseFloat(type.fee).toFixed(2)}
                            </span>
                          )}
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
            {formData.therapy_type_ids.length > 0 && (
              <div className="mt-2 flex items-center justify-between">
                <p className="text-xs text-gray-500">
                  {formData.therapy_type_ids.length} therapy type(s) selected
                </p>
                {formData.fee_per_session && (
                  <p className="text-xs font-medium text-primary-600">
                    Total Fee: ₹{parseFloat(formData.fee_per_session).toFixed(2)} per session
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Plan Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Plan Type <span className="text-red-500">*</span>
            </label>
            <select
              name="plan_type_id"
              value={formData.plan_type_id}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">Select plan type</option>
              {planTypes.map((pt) => (
                <option key={pt.id} value={pt.id}>
                  {pt.name}
                </option>
              ))}
            </select>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="start_date"
                value={formData.start_date}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End Date (Optional)
              </label>
              <input
                type="date"
                name="end_date"
                value={formData.end_date}
                onChange={handleChange}
                min={formData.start_date}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Start Time and End Time */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Time
                <span className="ml-2 text-xs font-normal text-gray-500">
                  (Required for duration calculation)
                </span>
              </label>
              <input
                type="time"
                name="start_time"
                value={formData.start_time}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End Time
                <span className="ml-2 text-xs font-normal text-gray-500">
                  (Required for duration calculation)
                </span>
              </label>
              <input
                type="time"
                name="end_time"
                value={formData.end_time}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Sessions Configuration */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sessions per Week
              </label>
              <input
                type="number"
                name="sessions_per_week"
                value={formData.sessions_per_week}
                onChange={handleChange}
                min="1"
                max="7"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Session Duration (minutes)
                <span className="ml-2 text-xs font-normal text-gray-500">
                  (Auto-calculated)
                </span>
              </label>
              <input
                type="number"
                name="session_duration"
                value={formData.session_duration}
                readOnly
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 cursor-not-allowed"
                title="This field is automatically calculated from start and end time"
              />
            </div>
          </div>

          {/* Fee */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Fee per Session (₹)
              {formData.therapy_type_ids.length > 0 && (
                <span className="ml-2 text-xs font-normal text-gray-500">
                  (Auto-calculated from selected therapy types)
                </span>
              )}
            </label>
            <input
              type="number"
              name="fee_per_session"
              value={formData.fee_per_session}
              onChange={handleChange}
              min="0"
              step="0.01"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Auto-calculated from therapy types"
            />
            {formData.therapy_type_ids.length > 0 && formData.fee_per_session && (
              <p className="mt-1 text-xs text-gray-500">
                You can manually adjust this amount if needed
              </p>
            )}
          </div>

          {/* Status (only for editing) */}
          {plan && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="active">Active</option>
                <option value="paused">Paused</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Additional notes about the therapy plan..."
            />
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Saving...' : plan ? 'Update Plan' : 'Create Plan'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TherapyPlanForm;

